/**
 * Central application configuration — tenant-safe.
 *
 * All environment-variable-backed constants live here so the rest of the
 * codebase can import them without duplicating fallback logic.
 *
 * TENANT RULE (2026-08-17, agentos-tenant-bootstrap): an unset or empty
 * VITE_* value NEVER falls back to an operator-owned endpoint
 * or credential. Empty means "not configured" — the feature hides itself
 * (Google login, 3D presenter, wave embeds) or resolves same-origin
 * (content-api, auth-api). Operator values are supplied by the operator's
 * own build env, never baked into source. Origin: Cloud/Alex tenant-
 * bootstrap for the David instance; previously STORAGE_API_KEY defaulted
 * to a live key and GOOGLE_CLIENT_ID to the operator's OAuth client, so
 * any tenant build that forgot a variable authenticated against the
 * operator's services.
 */

function env(name: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[name];
  return (v ?? '').trim().replace(/\/$/, '');
}

/** Same-origin base ('' → relative URLs like `/api/v1/...`). */
function sameOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// ── Required for a working instance ─────────────────────────────────

/** Content-API base (REST + WS derivation). Empty → same-origin, i.e. the
 *  tenant's nginx routes /api → content-api. */
export const CONTENT_API = env('VITE_CONTENT_API') || sameOrigin();

/** auth-api base. Empty → same-origin (/api/v1/auth → auth-api). */
export const AUTH_API = env('VITE_AUTH_API') || sameOrigin();

/** Origins the post-verification redirect may LEAVE this instance for
 *  (Post #4963: 3D-Presenter signups return to the presenter host).
 *  Mirrors auth-api's `signup_next_url_allowed_origins`; both ends enforce.
 *  Tenant-safe: empty → only same-origin targets are honored, any foreign
 *  absolute `next_url` falls back to "/". Comma-separated origins. */
export const REDIRECT_ALLOWED_ORIGINS: string[] = env('VITE_REDIRECT_ALLOWED_ORIGINS')
  .split(',')
  .map((s) => s.trim().replace(/\/$/, ''))
  .filter(Boolean);

/** Portale, die den Editor unter /frame/post/:id einbetten und ihm die
 *  Anmeldung per postMessage reichen dürfen (#2574). Strenger als
 *  VITE_EMBED_TRUSTED_ORIGINS der Leseansicht: dort stehen auch fremde
 *  Seiten, hier nur Portale. Leer → nur same-origin. Muss zur nginx-Liste
 *  `frame-ancestors` für /frame/ passen. */
export const FRAME_TRUSTED_ORIGINS: string[] = env('VITE_FRAME_TRUSTED_ORIGINS')
  .split(',')
  .map((s) => s.trim().replace(/\/$/, ''))
  .filter(Boolean)
  .concat(typeof window !== 'undefined' ? [window.location.origin] : []);

// ── Optional services — empty means "not available on this instance" ─

/** Storage API. Empty → uploads/media features are disabled. */
export const STORAGE_API = env('VITE_STORAGE_API');
export const STORAGE_API_KEY = env('VITE_STORAGE_API_KEY');
export const STORAGE_ENABLED = STORAGE_API !== '';

/** cloud-api (agent sessions / IACP UI hooks). Empty → hidden. */
export const CLOUD_API = env('VITE_CLOUD_API');
// Private Bilder (#5235) lädt die cloud-api hoch, die neben dem Storage
// steht und den privaten Medienweg eingerichtet hat. Ohne eigenen Wert
// gilt die cloud-api der Instanz.
export const PRIVATE_MEDIA_API = env('VITE_PRIVATE_MEDIA_API') || CLOUD_API;

/** 3D presenter. Empty → Presentation3DViewer is not offered. */
export const PRESENTER_3D = env('VITE_PRESENTER_3D');
export const PRESENTER_3D_ENABLED = PRESENTER_3D !== '';

/** Wave diagram embeds in markdown. Empty → ark-diagram blocks render as
 *  a plain notice instead of an iframe to an operator host. */
export const WAVE_API = env('VITE_WAVE_API');

/** Google OAuth client. Empty → Google login/signup buttons are hidden;
 *  the instance offers e-mail login only. NEVER falls back to the
 *  operator's client id. */
export const GOOGLE_CLIENT_ID = env('VITE_GOOGLE_CLIENT_ID');
export const GOOGLE_LOGIN_ENABLED = GOOGLE_CLIENT_ID !== '';

/** Adresse des Portals (CloudV2) dieser Instanz, z. B. `https://cloud2.kunde.example`.
 *  Die Anmeldung mit Microsoft läuft über den Rücksprung des Portals
 *  (Post #5420, Weg B): Content startet Microsoft mit
 *  `<PORTAL>/auth/microsoft/callback`, das Portal reicht `code` und `state`
 *  unverändert an `<Content>/auth/microsoft/callback` weiter, Content löst
 *  ein. Leer → kein Microsoft-Knopf, auch wenn die auth-api ihn anbietet;
 *  es gibt keinen Betreiber-Rückfall. */
export const PORTAL_WEB_ORIGIN = env('VITE_PORTAL_WEB_ORIGIN');
export const MICROSOFT_LOGIN_ENABLED = PORTAL_WEB_ORIGIN !== '';

// ── Constants ────────────────────────────────────────────────────────

export const SUPPORTED_LANGUAGES = ['de', 'en', 'sl', 'it', 'es'] as const;

// Derive WebSocket URL from the same host:
// https://content-api.example → wss://content-api.example
// http://localhost:8045 → ws://localhost:8045
export const CONTENT_WS = CONTENT_API
  .replace(/^http:/, 'ws:')
  .replace(/^https:/, 'wss:');
